import { useQueryClient } from '@tanstack/react-query';
import { SpaceNavigationView } from 'shared';

import { BackendRoute } from '../../../config/index';
import { QueryKey } from '../../../constants/index';
import { useAppQuery } from '../../../hooks/use-app-query.hook';
import { ApiService } from '../../../services/index';
import { selectedCompanyStorage } from '../../../storage/company/selected-company.util';
import { SPACES_STALE_TIME_MS } from '../constants/spaces-query.const';
import { FolderTreeScope, seedFolderLevel } from './use-space-navigation-folders.hook';

/** The sidebar roots; each root's first level is cached so opening it shows its folders at once. */
export const useSpacesNavigation = ({ enabled = true }: { enabled?: boolean } = {}) => {
  // Only keys the cache: the route takes the company from the session.
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);
  const queryClient = useQueryClient();

  const { data, isPending } = useAppQuery({
    queryKey: [QueryKey.GET_SPACES_NAVIGATION, companyId],
    queryFn: async () => {
      const navigation = await ApiService.get<SpaceNavigationView>({
        endpoint: BackendRoute.SPACES,
        routePath: ['navigation'],
      });

      for (const root of [navigation.vault, ...navigation.workspaces, ...navigation.dataRooms]) {
        seedFolderLevel(
          queryClient,
          companyId,
          FolderTreeScope.OWNER,
          { kind: root.kind, parentFolderId: null, spaceId: root.id },
          root.children,
        );
      }

      return navigation;
    },
    enabled: enabled && !!companyId,
    staleTime: SPACES_STALE_TIME_MS,
  });

  return { isPending: enabled && isPending, navigation: data };
};
